import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useEffect, useId, useState } from 'react'
import workflowInstancesAPI, {
  PlannedRoutine,
  WorkflowInstance,
} from '~/api/desktop/workflowInstances'
import { QUERY_KEYS } from '~/api/query-client-keys'
import Dialog from '~/components/Dialog'
import LoadingMessage from '~/components/LoadingMessage'
import TinyNotification from '~/components/notifications/TinyNotification'
import Table from '~/components/Table'
import Toaster from '~/components/Toaster'
import { formatTimestamp } from '~/utils/date'
import cs from './workflow_approval_details_dialog.scss'

const TIME_FORMAT = 'YYYY/MM/DD hh:mma'

interface WorkflowApprovalDetailsDialogProps {
  workflowInstance: WorkflowInstance | undefined
  isOpen: boolean
  onClose: () => void
  onApprovalAction?: () => void
  operatorName: string
}

const WorkflowApprovalDetailsDialog = ({
  workflowInstance,
  isOpen,
  onClose,
  onApprovalAction,
  operatorName,
}: WorkflowApprovalDetailsDialogProps) => {
  const [isRejectMode, setIsRejectMode] = useState(false)
  const [rejectionReason, setRejectionReason] = useState('')
  const textareaId = useId()
  const queryClient = useQueryClient()

  const {
    data: plannedRoutines,
    isLoading,
    error,
  } = useQuery({
    queryKey: [QUERY_KEYS.WORKFLOW_PLANNED_ROUTINES, workflowInstance?.uuid],
    queryFn: () => {
      if (!workflowInstance?.uuid) {
        throw new Error('No workflow instance UUID')
      }
      return workflowInstancesAPI.bulkRetrievePlannedRoutinesByWorkflowInstanceUuid(
        workflowInstance.uuid,
      )
    },
    enabled: !!workflowInstance?.uuid && isOpen,
    retry: 1,
  })

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setIsRejectMode(false)
      setRejectionReason('')
    }
  }, [isOpen])

  const approveMutation = useMutation({
    mutationFn: async (params: { uuid: string; operatorName: string }) => {
      if (!params.operatorName.trim()) {
        throw new Error('Operator name is required')
      }
      return await workflowInstancesAPI.approve(params.uuid, params.operatorName)
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.WORKFLOW_INSTANCES_PENDING],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.WORKFLOW_INSTANCES_ACTIVE],
        }),
      ])

      Toaster.show({
        message: `Workflow "${workflowInstance?.workflow_definition_id}" approved successfully`,
        intent: 'success',
      })

      try {
        onApprovalAction?.()
        onClose()
      } catch (callbackError) {
        console.error('Error in approval callback:', callbackError)
      }
    },
    onError: error => {
      Toaster.show({
        message: `Failed to approve workflow: ${error instanceof Error ? error.message : String(error)}`,
        intent: 'danger',
      })
    },
  })

  const rejectMutation = useMutation({
    mutationFn: async (params: {
      uuid: string
      operatorName: string
      reason: string
    }) => {
      if (!params.reason.trim()) {
        throw new Error('Rejection reason is required')
      }
      return await workflowInstancesAPI.reject(
        params.uuid,
        params.operatorName,
        params.reason.trim(),
      )
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.WORKFLOW_INSTANCES_PENDING],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.WORKFLOW_INSTANCES_ACTIVE],
        }),
      ])

      Toaster.show({
        message: `Workflow "${workflowInstance?.workflow_definition_id}" rejected`,
        intent: 'success',
      })

      try {
        onApprovalAction?.()
        onClose()
      } catch (callbackError) {
        console.error('Error in rejection callback:', callbackError)
      }
    },
    onError: error => {
      Toaster.show({
        message: `Failed to reject workflow: ${error instanceof Error ? error.message : String(error)}`,
        intent: 'danger',
      })
    },
  })

  const handleApprove = () => {
    if (!workflowInstance) return
    if (!operatorName.trim()) {
      Toaster.show({
        message: 'Operator identity is required to approve workflow',
        intent: 'danger',
      })
      return
    }
    approveMutation.mutate({ uuid: workflowInstance.uuid, operatorName })
  }

  const handleReject = () => {
    if (!workflowInstance) return

    if (!isRejectMode) {
      setIsRejectMode(true)
      return
    }

    if (!rejectionReason.trim()) {
      Toaster.show({
        message: 'Please provide a reason for rejection',
        intent: 'danger',
      })
      return
    }

    rejectMutation.mutate({
      uuid: workflowInstance.uuid,
      operatorName,
      reason: rejectionReason,
    })
  }

  const handleCancel = () => {
    if (isRejectMode) {
      setIsRejectMode(false)
      setRejectionReason('')
    } else {
      onClose()
    }
  }

  const formatRelativeTime = (minutes: number): string => {
    if (minutes === 0) return 'T+0'
    if (minutes < 60) {
      return `T+${Math.round(minutes)}min`
    }
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = Math.round(minutes % 60)
    if (remainingMinutes === 0) {
      return `T+${hours}h`
    }
    return `T+${hours}h ${remainingMinutes}min`
  }

  const getProjectedStartTime = (relativeMinutes: number): string => {
    if (workflowInstance?.earliest_start_time) {
      const projectedTime = dayjs(workflowInstance.earliest_start_time).add(
        relativeMinutes,
        'minute',
      )
      return projectedTime.format(TIME_FORMAT)
    }
    return formatRelativeTime(relativeMinutes)
  }

  const tableColumns = [
    {
      name: 'Routine',
      width: 250,
      render: (plannedRoutine: PlannedRoutine) => (
        <div>
          <div className={cs.routineName}>{plannedRoutine.node_key}</div>
          <div className={cs.routineInfo}>{plannedRoutine.routine_name}</div>
        </div>
      ),
    },
    {
      name: 'Parameters',
      width: 'flex',
      render: (plannedRoutine: PlannedRoutine) => (
        <pre className={cs.parametersJson}>
          {JSON.stringify(plannedRoutine.routine_parameters, null, 2)}
        </pre>
      ),
    },
    {
      name: 'Planned Start At',
      width: 180,
      render: (plannedRoutine: PlannedRoutine) => (
        <div>
          {getProjectedStartTime(plannedRoutine.relative_execution_time_minutes)}
        </div>
      ),
    },
  ]

  const renderContainer = () => {
    if (isLoading) {
      return (
        <div className={cs.container}>
          <LoadingMessage
            label='Loading Planned Routines...'
            className={cs.loadingMessageBox}
          />
        </div>
      )
    }
    if (error) {
      return (
        <div className={cs.container}>
          <TinyNotification
            type='bareError'
            message={
              error instanceof Error ? error.message : 'Could not load planned routines'
            }
          />
        </div>
      )
    }
    if (plannedRoutines) {
      return (
        <div className={cs.contentWrapper}>
          <div className={cs.metadataSection}>
            <div className={cs.metadataRow}>
              <span className={cs.label}>Requested by:</span>
              <span className={cs.value}>
                {workflowInstance?.requested_by || 'Unknown'}
              </span>
            </div>
            <div className={cs.metadataRow}>
              <span className={cs.label}>Reason:</span>
              <span className={cs.value}>
                {workflowInstance?.requested_reason || 'No reason provided'}
              </span>
            </div>
            {workflowInstance?.earliest_start_time && (
              <div className={cs.metadataRow}>
                <span className={cs.label}>Earliest start:</span>
                <span className={cs.value}>
                  {formatTimestamp(workflowInstance.earliest_start_time)}
                </span>
              </div>
            )}
          </div>
          <Table
            className={cs.table}
            columns={tableColumns}
            data={plannedRoutines}
            rowLeftRightPaddingVariant={'rowPaddingLow'}
            heightSizing={'flexAuto'}
            rowKey={'node_key'}
          />
        </div>
      )
    }
  }

  const isPending = approveMutation.isPending || rejectMutation.isPending

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleCancel}
      className={cs.workflowApprovalDetailsDialog}
    >
      <div className={cs.title}>
        Workflow Approval: {workflowInstance?.workflow_definition_id || 'Workflow'}
      </div>
      {renderContainer()}
      <Dialog.Footer
        loadingMessage={
          approveMutation.isPending
            ? 'Approving Workflow'
            : rejectMutation.isPending
              ? 'Rejecting Workflow'
              : undefined
        }
      >
        <div className={cs.footerContent}>
          {isRejectMode ? (
            <div className={cs.rejectionSection}>
              <div className={cs.rejectionInputWrapper}>
                <label className={cs.rejectionLabel} htmlFor={textareaId}>
                  Rejection Reason <span className={cs.required}>*</span>
                </label>
                <textarea
                  id={textareaId}
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e.target.value)}
                  placeholder='Explain why this workflow is being rejected...'
                  rows={2}
                  className={cs.rejectionTextarea}
                  autoFocus
                  disabled={isPending}
                />
              </div>
              <div className={cs.footerButtons}>
                <Dialog.FooterButton
                  label='Back'
                  onClick={handleCancel}
                  disabled={isPending}
                />
                <Dialog.FooterButton
                  type='danger'
                  label='Confirm Rejection'
                  onClick={handleReject}
                  disabled={isPending || !rejectionReason.trim()}
                />
                <Dialog.FooterButton
                  type='primary'
                  label='Approve'
                  onClick={handleApprove}
                  disabled={isPending || isRejectMode || !operatorName.trim()}
                />
              </div>
            </div>
          ) : (
            <div className={cs.operatorInfoRow}>
              <div className={cs.operatorInfo}>
                <span className={cs.operatorLabel}>Approving as:</span>
                <span className={cs.operatorName}>{operatorName}</span>
              </div>
              <div className={cs.footerButtons}>
                <Dialog.FooterButton
                  label='Cancel'
                  onClick={handleCancel}
                  disabled={isPending}
                />
                <Dialog.FooterButton
                  type='danger'
                  label='Reject'
                  onClick={handleReject}
                  disabled={isPending}
                />
                <Dialog.FooterButton
                  type='primary'
                  label='Approve'
                  onClick={handleApprove}
                  disabled={isPending || !operatorName.trim()}
                />
              </div>
            </div>
          )}
        </div>
      </Dialog.Footer>
    </Dialog>
  )
}

export default WorkflowApprovalDetailsDialog
